import { noticias as estaticas, type NoticiaPublica } from '../data/noticias';
import { getPublishedNews, type News } from './newsService';

/**
 * Noticias visibles en la web pública, ordenadas de más a menos reciente.
 *
 * Junta las dos procedencias que hay:
 *
 * - las **estáticas**, que viven en `src/data/noticias.ts` dentro del código;
 * - las **publicadas** desde el panel, que salen de `newsService` y que, en
 *   cuanto se configure `VITE_API_BASE_URL`, vendrán del backend.
 *
 * Lo usan tanto la página de Noticias como la sección del home, de modo que
 * ambas enseñan lo mismo y el día que entre el API ninguna de las dos necesita
 * cambios.
 */

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
] as const;

const LONGITUD_RESUMEN = 150;

/**
 * Fecha en el formato que usan las noticias del código: «16 julio, 2026».
 *
 * Las creadas desde el panel se formatean igual a propósito: con
 * `toLocaleDateString` salían como «16/7/2026» y en el mismo listado convivían
 * dos formatos distintos.
 */
export function formatearFecha(ms: number): string {
  const fecha = new Date(ms);
  if (Number.isNaN(fecha.getTime())) return '';

  return `${fecha.getDate()} ${MESES[fecha.getMonth()]}, ${fecha.getFullYear()}`;
}

/** «16 julio, 2026» → milisegundos, para poder ordenar. */
export function fechaEnMilisegundos(fecha: string): number {
  const [dia, mes, anio] = fecha.toLowerCase().split(/[\s,]+/).filter(Boolean);
  const indiceMes = MESES.findIndex((nombre) => nombre === mes);

  if (indiceMes === -1) return Number.NaN;

  return new Date(Number(anio), indiceMes, Number(dia)).getTime();
}

/**
 * Texto plano de un fragmento de HTML, para recortar el resumen.
 *
 * `DOMParser` en vez de `innerHTML` sobre un `<div>`: aunque ese `<div>` nunca
 * se cuelga del documento, el navegador igualmente puede llegar a pedir sus
 * recursos (`<img src>`) y disparar sus manejadores inline (`onerror`, etc.) —
 * es el vector de XSS "innerHTML en nodo desconectado", que parece inofensivo
 * y no lo es. Un documento de `DOMParser` no ejecuta scripts ni carga nada.
 */
function textoPlano(html: string): string {
  const documento = new DOMParser().parseFromString(html, 'text/html');
  return documento.body.textContent ?? '';
}

/** Noticia del panel → la forma que consumen las tarjetas y el detalle. */
export function noticiaDesdeNews(news: News): NoticiaPublica {
  const plano = textoPlano(news.content).trim();
  const resumen =
    news.excerpt ??
    (plano.length > LONGITUD_RESUMEN
      ? `${plano.substring(0, LONGITUD_RESUMEN).trim()}...`
      : plano);

  return {
    id: news.id,
    title: news.title,
    excerpt: resumen,
    image: news.imageData,
    date: formatearFecha(news.publishedAt ?? news.createdAt),
    content: news.content,
    featured: news.featured === true,
  };
}

/**
 * Valor para el primer render, sin esperar al servicio.
 *
 * Las estáticas ya están en el código y vienen ordenadas, así que la página
 * puede pintarse entera antes de que responda el API.
 */
export const noticiasIniciales = (): NoticiaPublica[] => [...estaticas];

/** Todas las noticias visibles, de más a menos reciente. */
export async function getNoticiasPublicas(signal?: AbortSignal): Promise<NoticiaPublica[]> {
  const publicadas = await getPublishedNews(signal);

  // Se ordena por la fecha en milisegundos y no reinterpretando el texto que se
  // pinta: es el dato de verdad y no depende del formato.
  const conFecha = [
    ...estaticas.map((noticia) => ({
      noticia: noticia as NoticiaPublica,
      ms: fechaEnMilisegundos(noticia.date),
    })),
    ...publicadas.map((news) => ({
      noticia: noticiaDesdeNews(news),
      ms: news.publishedAt ?? news.createdAt,
    })),
  ];

  conFecha.sort((a, b) => b.ms - a.ms);

  return conFecha.map((entrada) => entrada.noticia);
}

/**
 * La destacada del listado.
 *
 * Manda la marcada desde el panel; si hay varias, gana la más reciente porque
 * la lista ya viene ordenada. Sin ninguna marcada, la más reciente.
 */
export const getDestacada = (noticias: NoticiaPublica[]): NoticiaPublica | undefined =>
  noticias.find((noticia) => noticia.featured === true) ?? noticias[0];
